import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker } from './shared';

/**
 * BADGE
 *
 * A short, non-interactive status marker. `tone` carries the meaning; the
 * `solid` / `subtle` / `outline` appearances let the same meaning be loud or
 * quiet depending on how much of the screen is already coloured.
 *
 * Subtle is the default: a call log full of solid badges is unreadable.
 *
 * `tone` holds two conceptually different scales, because a badge has exactly
 * one colour and modelling them as two variants would just make them fight over
 * `bg-*`:
 *
 *   - severity  — neutral / primary / success / warning / info / destructive
 *   - job phase — the `phase-*` values, which say where a job has got to and
 *                 carry no severity at all
 *
 * Components take them as separate props (`tone` and `phase`) and resolve to one
 * value here. See the JOB PHASE block in tokens.css.
 */
export const badgeRecipe = cva(
  [
    uiMarker,
    'inline-flex items-center gap-1 whitespace-nowrap rounded-pill font-sans font-medium [&_svg]:size-icon-xs [&_svg]:shrink-0',
  ],
  {
    variants: {
      tone: {
        neutral: '',
        primary: '',
        success: '',
        warning: '',
        info: '',
        processing: '',
        destructive: '',
        'phase-ready': '',
        'phase-dispatched': '',
        'phase-en-route': '',
        'phase-on-scene': '',
        'phase-towing': '',
        'phase-returning': '',
        'phase-complete': '',
      },
      appearance: {
        solid: 'border border-transparent',
        subtle: 'border border-transparent',
        outline: 'border bg-transparent',
      },
      size: {
        sm: 'px-2 py-0.5 text-overline',
        md: 'px-2.5 py-0.5 text-caption',
        lg: 'px-3 py-1 text-body-sm',
        /** The phone's status chip: a call's status in a row or a header. */
        xl: 'h-7 px-3 text-body-strong',
        /** A status offered as a choice, in the phone's status sheet. */
        '2xl': 'px-3 py-2 text-body-lg-strong',
      },
      /**
       * Let a long label wrap rather than run off a narrow screen: the
       * "Made for <company> · <town>" chip at the top of the sales page.
       */
      wrap: {
        true: 'whitespace-normal',
        false: '',
      },
    },
    compoundVariants: [
      // Solid — highest contrast, for the one status that must be noticed.
      {
        appearance: 'solid',
        tone: 'neutral',
        class: 'bg-muted text-foreground',
      },
      {
        appearance: 'solid',
        tone: 'primary',
        class: 'bg-primary text-primary-foreground',
      },
      {
        appearance: 'solid',
        tone: 'success',
        class: 'bg-success text-success-foreground',
      },
      {
        appearance: 'solid',
        tone: 'warning',
        class: 'bg-warning text-warning-foreground',
      },
      {
        appearance: 'solid',
        tone: 'info',
        class: 'bg-info text-info-foreground',
      },
      {
        appearance: 'solid',
        tone: 'processing',
        class: 'bg-processing text-processing-foreground',
      },
      {
        appearance: 'solid',
        tone: 'destructive',
        class: 'bg-destructive text-destructive-foreground',
      },

      // Subtle — tinted background, readable dark text. The workhorse.
      {
        appearance: 'subtle',
        tone: 'neutral',
        class: 'bg-muted text-muted-foreground',
      },
      {
        appearance: 'subtle',
        tone: 'primary',
        class: 'bg-primary/10 text-primary',
      },
      {
        appearance: 'subtle',
        tone: 'success',
        class: 'bg-success-subtle text-success-subtle-foreground',
      },
      {
        appearance: 'subtle',
        tone: 'warning',
        class: 'bg-warning-subtle text-warning-subtle-foreground',
      },
      {
        appearance: 'subtle',
        tone: 'info',
        class: 'bg-info-subtle text-info-subtle-foreground',
      },
      {
        appearance: 'subtle',
        tone: 'processing',
        class: 'bg-processing-subtle text-processing-subtle-foreground',
      },
      {
        appearance: 'subtle',
        tone: 'destructive',
        class: 'bg-destructive-subtle text-destructive-subtle-foreground',
      },

      // Outline — quietest. For counts and metadata that are not really status.
      {
        appearance: 'outline',
        tone: 'neutral',
        class: 'border-border text-muted-foreground',
      },
      {
        appearance: 'outline',
        tone: 'primary',
        class: 'border-primary/40 text-primary',
      },
      {
        appearance: 'outline',
        tone: 'success',
        class: 'border-success/40 text-success',
      },
      {
        appearance: 'outline',
        tone: 'warning',
        class: 'border-warning/40 text-warning',
      },
      {
        appearance: 'outline',
        tone: 'info',
        class: 'border-info/40 text-info',
      },
      {
        appearance: 'outline',
        tone: 'processing',
        class: 'border-processing/40 text-processing',
      },
      {
        appearance: 'outline',
        tone: 'destructive',
        class: 'border-destructive/40 text-destructive',
      },

      /*
       * Job phase. Same three appearances, one entry per phase — verbose, but
       * it keeps every colour decision visible in one file rather than hidden
       * behind a loop, which is the point of the recipes.
       */
      {
        appearance: 'solid',
        tone: 'phase-dispatched',
        class: 'bg-phase-dispatched text-phase-dispatched-foreground',
      },
      {
        appearance: 'solid',
        tone: 'phase-en-route',
        class: 'bg-phase-en-route text-phase-en-route-foreground',
      },
      {
        appearance: 'solid',
        tone: 'phase-on-scene',
        class: 'bg-phase-on-scene text-phase-on-scene-foreground',
      },
      {
        appearance: 'solid',
        tone: 'phase-towing',
        class: 'bg-phase-towing text-phase-towing-foreground',
      },
      {
        appearance: 'solid',
        tone: 'phase-returning',
        class: 'bg-phase-returning text-phase-returning-foreground',
      },
      {
        appearance: 'solid',
        tone: 'phase-ready',
        class: 'bg-phase-ready text-phase-ready-foreground',
      },
      {
        appearance: 'solid',
        tone: 'phase-complete',
        class: 'bg-phase-complete text-phase-complete-foreground',
      },

      {
        appearance: 'subtle',
        tone: 'phase-dispatched',
        class:
          'bg-phase-dispatched-subtle text-phase-dispatched-subtle-foreground',
      },
      {
        appearance: 'subtle',
        tone: 'phase-en-route',
        class: 'bg-phase-en-route-subtle text-phase-en-route-subtle-foreground',
      },
      {
        appearance: 'subtle',
        tone: 'phase-on-scene',
        class: 'bg-phase-on-scene-subtle text-phase-on-scene-subtle-foreground',
      },
      {
        appearance: 'subtle',
        tone: 'phase-towing',
        class: 'bg-phase-towing-subtle text-phase-towing-subtle-foreground',
      },
      {
        appearance: 'subtle',
        tone: 'phase-returning',
        class:
          'bg-phase-returning-subtle text-phase-returning-subtle-foreground',
      },
      {
        appearance: 'subtle',
        tone: 'phase-ready',
        class: 'bg-phase-ready-subtle text-phase-ready-subtle-foreground',
      },
      {
        appearance: 'subtle',
        tone: 'phase-complete',
        class: 'bg-phase-complete-subtle text-phase-complete-subtle-foreground',
      },

      {
        appearance: 'outline',
        tone: 'phase-dispatched',
        class: 'border-phase-dispatched/40 text-phase-dispatched',
      },
      {
        appearance: 'outline',
        tone: 'phase-en-route',
        class: 'border-phase-en-route/40 text-phase-en-route',
      },
      {
        appearance: 'outline',
        tone: 'phase-on-scene',
        class: 'border-phase-on-scene/40 text-phase-on-scene',
      },
      {
        appearance: 'outline',
        tone: 'phase-towing',
        class: 'border-phase-towing/40 text-phase-towing',
      },
      {
        appearance: 'outline',
        tone: 'phase-returning',
        class: 'border-phase-returning/40 text-phase-returning',
      },
      {
        appearance: 'outline',
        tone: 'phase-ready',
        class: 'border-phase-ready/40 text-phase-ready',
      },
      {
        appearance: 'outline',
        tone: 'phase-complete',
        class: 'border-phase-complete/40 text-phase-complete',
      },
    ],
    defaultVariants: {
      tone: 'neutral',
      appearance: 'subtle',
      size: 'md',
      wrap: false,
    },
  }
);

export type BadgeRecipeProps = VariantProps<typeof badgeRecipe>;

/** The fill of a status dot in each tone, outside a badge (StatusDot). */
const dotTones = {
  neutral: 'bg-muted-foreground',
  primary: 'bg-primary',
  success: 'bg-success',
  warning: 'bg-warning',
  info: 'bg-info',
  processing: 'bg-processing',
  destructive: 'bg-destructive',
  'phase-dispatched': 'bg-phase-dispatched',
  'phase-en-route': 'bg-phase-en-route',
  'phase-on-scene': 'bg-phase-on-scene',
  'phase-towing': 'bg-phase-towing',
  'phase-returning': 'bg-phase-returning',
  'phase-ready': 'bg-phase-ready',
  'phase-complete': 'bg-phase-complete',
} as const;

/**
 * A status dot on its own, outside a badge: the circle a Chip leads with, or
 * the fault mark in front of a time in a list row, at the sizes those sit
 * at. Status pills carry no dot: their fill is the colour (the owner,
 * 2026-10-29).
 */
export const statusDotRecipe = cva([uiMarker, 'shrink-0 rounded-pill'], {
  variants: {
    tone: dotTones,
    size: {
      /** In front of a time in a list row. */
      sm: 'size-2',
      /** Where a Chip's 16px glyph would be. */
      md: 'size-icon-sm',
      /** In the phone's duty-status chip and status control: 14px. */
      chip: 'size-3.5',
    },
  },
  defaultVariants: { tone: 'neutral', size: 'md' },
});

export type StatusDotRecipeProps = VariantProps<typeof statusDotRecipe>;

/**
 * CHIP
 *
 * A fact about the thing on screen, said in four words beside its title —
 * "Passed", "Daily walk-around", "Marcus Webb, driver". Not a Badge: a Badge
 * is a pill at 12px with a 12px icon, and these are 25-high rounded squares
 * carrying 14px text and a 16px glyph. Same tokens, different shape.
 */
export const chipRecipe = cva([
  uiMarker,
  'inline-flex items-center gap-1 rounded-lg bg-muted px-2 py-1 font-sans text-label text-foreground',
  '[&_svg]:size-icon-sm [&_svg]:shrink-0',
]);
